import React, { useState, useEffect } from 'react';
import { FormSchema, SchemaUpdateRequest, Question, FormStatus } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { COLORS } from '../../../../core/constants/colors';

interface SchemaDrawerProps {
  mode: 'view' | 'edit';
  schema: FormSchema | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const SchemaDrawer: React.FC<SchemaDrawerProps> = ({
  mode,
  schema,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<SchemaUpdateRequest>({
    form_name: '',
    form_description: '',
    question_ids: [],
    status: 'draft',
    changes_description: '',
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && schema) {
      setFormData({
        form_name: schema.form_name,
        form_description: schema.form_description || '',
        question_ids: schema.question_ids,
        status: schema.status,
        changes_description: '',
      });
      
      if (mode === 'edit') {
        // Load all active questions for editing
        loadAllQuestions();
      } else {
        // Load only schema questions for viewing
        if (schema.questions) {
          setQuestions(schema.questions);
        } else {
          loadQuestions(schema.question_ids);
        }
      }
      setError('');
    }
  }, [isOpen, schema, mode]);

  const loadQuestions = async (questionIds: number[]) => {
    try {
      const allQuestions = await adminFormsService.getAllQuestions();
      const schemaQuestions = allQuestions.filter(q => questionIds.includes(q.id));
      setQuestions(schemaQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const loadAllQuestions = async () => {
    try {
      const data = await adminFormsService.getAllQuestions({ is_active: true });
      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!schema || formData.question_ids?.length === 0) {
      setError('Please select at least one question');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adminFormsService.updateSchema(schema.id, formData);
      if (result) {
        onSuccess();
        onClose();
      } else {
        setError('Failed to update schema');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleQuestion = (questionId: number) => {
    setFormData(prev => ({
      ...prev,
      question_ids: prev.question_ids?.includes(questionId)
        ? prev.question_ids.filter(id => id !== questionId)
        : [...(prev.question_ids || []), questionId]
    }));
  };

  const handleDiscard = () => {
    if (schema) {
      setFormData({
        form_name: schema.form_name,
        form_description: schema.form_description || '',
        question_ids: schema.question_ids,
        status: schema.status,
        changes_description: '',
      });
    }
    setError('');
    onClose();
  };

  if (!isOpen || !schema) return null;

  const title = mode === 'edit' ? 'Edit Schema' : 'Schema Details';

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, { bg: string; text: string }> = {
      active: { bg: '#D1FAE5', text: '#059669' },
      draft: { bg: '#FEF3C7', text: '#D97706' },
      archived: { bg: '#F3F4F6', text: '#6B7280' },
    };
    return statusColors[status] || { bg: '#F3F4F6', text: '#6B7280' };
  };

  const statusBadge = getStatusBadge(schema.status);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleDiscard}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              {title}
            </h2>
            <button
              onClick={handleDiscard}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {mode === 'view' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Form Name
                    </label>
                    <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                      {schema.form_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Company ID
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {schema.company_id}
                    </p>
                  </div>
                </div>

                {schema.form_description && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Description
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {schema.form_description}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Generation Method
                    </label>
                    <p className="text-base font-semibold capitalize" style={{ color: '#1F2937' }}>
                      {schema.generation_method.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Status
                    </label>
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium inline-block"
                      style={{
                        backgroundColor: statusBadge.bg,
                        color: statusBadge.text,
                      }}
                    >
                      {schema.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Version
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {schema.version}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Current Version
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {schema.is_current_version ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>

                {schema.rag_analysis_summary && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      RAG Analysis Summary
                    </label>
                    <p className="text-base p-3 rounded-lg bg-gray-50" style={{ color: '#6B7280' }}>
                      {schema.rag_analysis_summary}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Questions ({questions.length})
                  </label>
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                    {questions.length === 0 ? (
                      <p className="text-sm" style={{ color: '#9CA3AF' }}>No questions found</p>
                    ) : (
                      <div className="space-y-3">
                        {questions.map((question) => (
                          <div
                            key={question.id}
                            className="p-3 rounded-lg bg-gray-50"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}>
                                {question.category}
                              </span>
                              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}>
                                {question.field_type}
                              </span>
                            </div>
                            <p className="text-sm font-medium" style={{ color: '#1F2937' }}>
                              {question.label}
                            </p>
                            {question.description && (
                              <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                                {question.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Created At
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {new Date(schema.created_at).toLocaleString()}
                    </p>
                  </div>
                  {schema.updated_at && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                        Last Updated
                      </label>
                      <p className="text-base" style={{ color: '#6B7280' }}>
                        {new Date(schema.updated_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Form Name *
                  </label>
                  <input
                    type="text"
                    value={formData.form_name || ''}
                    onChange={(e) => setFormData({ ...formData, form_name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Form Description
                  </label>
                  <textarea
                    value={formData.form_description || ''}
                    onChange={(e) => setFormData({ ...formData, form_description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Status *
                  </label>
                  <select
                    value={formData.status || 'draft'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as FormStatus })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Select Questions * ({formData.question_ids?.length || 0} selected)
                  </label>
                  <div className="border rounded-lg p-4 max-h-64 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                    {questions.length === 0 ? (
                      <p className="text-sm" style={{ color: '#9CA3AF' }}>No questions available</p>
                    ) : (
                      <div className="space-y-2">
                        {questions.map((question) => (
                          <label
                            key={question.id}
                            className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.question_ids?.includes(question.id) || false}
                              onChange={() => toggleQuestion(question.id)}
                              className="mt-1 w-4 h-4 rounded"
                              style={{ accentColor: COLORS.admin.primary }}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium" style={{ color: '#1F2937' }}>
                                {question.label}
                              </p>
                              {question.description && (
                                <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                                  {question.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}>
                                  {question.category}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}>
                                  {question.field_type}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Changes Description
                  </label>
                  <textarea
                    value={formData.changes_description || ''}
                    onChange={(e) => setFormData({ ...formData, changes_description: e.target.value })}
                    rows={2}
                    placeholder="Describe what changed in this version..."
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                {/* Action Buttons */}
                <div className="pt-6 space-y-3">
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: COLORS.admin.primary }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Updating...' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscard}
                    className="w-full py-2 text-sm font-medium hover:underline"
                    style={{ color: '#6B7280' }}
                    disabled={isSubmitting}
                  >
                    Discard changes
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

